import { doc, getDoc } from 'firebase/firestore'
import { apiPost } from '@/composables/useApi'
import { useMaterials } from '@/composables/useMaterials'
import { db } from '@/firebase'

export class StudySourceMissingError extends Error {}
export class StudySourceNotReadyError extends Error {}

export function isMaterialSource(source) {
  return String(source.audioId || '').startsWith('materials-')
}

export function canOpenStudySource(source) {
  if (!source?.audioId) return false
  return !isMaterialSource(source) || Boolean(source.materialId && source.materialBook)
}

// 打开学习记录的原始文件或素材；返回 AudioPlayer 所需的视图参数
export async function resolveStudySource(source, translate) {
  if (!canOpenStudySource(source)) throw new StudySourceMissingError()
  return isMaterialSource(source)
    ? resolveMaterialSource(source, translate)
    : resolveFileSource(source)
}

async function resolveFileSource(source) {
  const snapshot = await getDoc(doc(db, 'files', source.audioId))
  if (!snapshot.exists()) throw new StudySourceMissingError()

  const file = { id: snapshot.id, ...snapshot.data() }
  if (file.status !== 'completed') throw new StudySourceNotReadyError()

  const youtubeVideoId = file.sourceType === 'youtube' ? file.youtube?.videoId || '' : ''
  const audioSrc = youtubeVideoId
    ? ''
    : (await apiPost('/api/get-signed-url', { filePath: file.filePath, fileId: file.id })).url

  return {
    audioSrc,
    youtubeVideoId,
    posterSrc: youtubeVideoId ? file.youtube.thumbnailUrl : '',
    audioId: file.id,
    audioName: file.name || source.audioName,
    taskType: file.type || 'stt',
    originalSubtitles: file.originalSubtitles,
    subtitles: file.subtitles,
    practiceSubtitles: file.practiceSubtitles,
    shortPracticeSubtitles: file.shortPracticeSubtitles,
    pauseSubtitles: file.pauseSubtitles,
    completeSubtitles: file.completeSubtitles,
    words: file.words,
    originalText: file.originalText,
    filePath: file.filePath,
    duration: file.duration,
    materialId: file.materialId,
    materialBook: file.materialBook,
  }
}

async function resolveMaterialSource(source, translate) {
  const categoryId = source.audioId.replace(/^materials-/, '')
  const { fetchMaterialDetail } = useMaterials(translate)
  let material
  try {
    material = await fetchMaterialDetail({
      categoryId,
      collectionId: source.materialBook,
      item: { id: source.materialId },
    })
  } catch (error) {
    if (/not found|不存在/i.test(error.message || '')) throw new StudySourceMissingError()
    throw error
  }

  const youtubeVideoId = material.youtubeVideoId || ''
  // 播客素材直接播原地址，不经过我们的存储
  const externalAudio = material.sourceType === 'podcast'
  const audioSrc = youtubeVideoId
    ? ''
    : externalAudio
    ? material.audioUrl
    : (await apiPost('/api/get-signed-url', {
        filePath: material.filePath,
        categoryId,
        collectionId: source.materialBook,
        itemId: source.materialId,
      })).url
  const audioTitle = material.title || material.name || source.audioName
  return {
    audioSrc,
    youtubeVideoId,
    audioId: source.audioId,
    cover: { src: material.image, artwork: externalAudio },
    audioName: material.description ? `${audioTitle} - ${material.description}` : audioTitle,
    taskType: material.type || 'stt',
    originalSubtitles: material.originalSubtitles,
    subtitles: material.subtitles,
    practiceSubtitles: material.practiceSubtitles,
    shortPracticeSubtitles: material.shortPracticeSubtitles,
    pauseSubtitles: material.pauseSubtitles,
    completeSubtitles: material.completeSubtitles,
    words: material.words,
    originalText: material.originalText,
    filePath: material.filePath,
    duration: material.durationSeconds || material.duration,
    materialId: source.materialId,
    materialBook: source.materialBook,
    allowAudioDownload: !externalAudio,
  }
}
